class CustomPredictiveSearch {
  constructor(container) {
    this.container = container;
    this.input = container.querySelector('[data-predictive-search-input]');
    this.resultsContainer = container.querySelector('[data-predictive-search-results]');
    this.abortController = null;
    this.debounceTimer = null;
    this.isOpen = false;

    if (!this.input || !this.resultsContainer) return;

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onClickOutside.bind(this));
  }

  onInput() {
    clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();

    if (!query.length) {
      this.close();
      return;
    }

    // Show loading state
    this.resultsContainer.innerHTML = `
      <div class="predictive-loading">
        <div class="spinner"></div>
        <span>Searching...</span>
      </div>
    `;
    this.open();

    // Debounce the API call by 300ms
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, 300);
  }

  onFocus() {
    const query = this.input.value.trim();
    if (query.length && this.resultsContainer.children.length > 0) {
      this.open();
    }
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      this.input.blur();
    }
  }

  onClickOutside(e) {
    if (!this.container.contains(e.target)) {
      this.close();
    }
  }

  open() {
    this.resultsContainer.classList.add('is-active');
    this.isOpen = true;
  }

  close() {
    this.resultsContainer.classList.remove('is-active');
    this.resultsContainer.innerHTML = '';
    this.isOpen = false;
  }

  fetchResults(query) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    fetch(
      `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[limit]=8&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('#shopify-section-predictive-search');

        if (content) {
          this.renderResults(content, query);
        } else {
          this.resultsContainer.innerHTML = `
            <div class="predictive-no-results">No results found for "${query}"</div>
          `;
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          this.resultsContainer.innerHTML = '';
          this.close();
        }
      });
  }

  renderResults(content, query) {
    // Extract product data from the predictive search section response
    const products = content.querySelectorAll('.predictive-search__list--products .predictive-search__list-item');

    if (products.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="predictive-no-results">No results found for "${query}"</div>
      `;
      this.open();
      return;
    }

    let resultsHTML = '<ul class="predictive-results-list">';

    products.forEach(product => {
      // Get link from the product card
      const linkEl = product.querySelector('a[data-product-link]') || product.querySelector('a');
      const href = linkEl ? (linkEl.getAttribute('href') || '#') : '#';

      // Get the first product image
      const img = product.querySelector('img');
      let imgSrc = '';
      if (img) {
        imgSrc = img.getAttribute('src') || '';
        if (!imgSrc) {
          const srcset = img.getAttribute('srcset');
          if (srcset) imgSrc = srcset.split(',')[0].trim().split(' ')[0];
        }
      }

      // Get title from product-card__title
      const titleEl = product.querySelector('.product-card__title') || product.querySelector('h3') || product.querySelector('h2');
      const titleText = titleEl ? titleEl.textContent.trim() : 'Product';

      // Get price from product-price
      const priceEl = product.querySelector('.product-price');
      let priceText = '';
      if (priceEl) {
        // Clean up the price text — remove extra whitespace and hidden text
        const priceClone = priceEl.cloneNode(true);
        // Remove hidden elements
        priceClone.querySelectorAll('.display--hidden, [hidden]').forEach(el => el.remove());
        priceText = priceClone.textContent.replace(/\s+/g, ' ').trim();
      }

      resultsHTML += `
        <li class="predictive-result-item">
          <a href="${href}" class="predictive-result-link">
            ${imgSrc ? `<img class="predictive-result-image" src="${imgSrc}" alt="${titleText}" loading="lazy">` : ''}
            <div class="predictive-result-info">
              <span class="predictive-result-title">${titleText}</span>
              ${priceText ? `<span class="predictive-result-price">${priceText}</span>` : ''}
            </div>
          </a>
        </li>
      `;
    });

    resultsHTML += '</ul>';
    resultsHTML += `<a href="/search?q=${encodeURIComponent(query)}" class="predictive-view-all">View all results →</a>`;

    this.resultsContainer.innerHTML = resultsHTML;
    this.open();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('predictive-search')
    .forEach(el => new CustomPredictiveSearch(el));
});
